import React, { useMemo } from "react";
import { Card, SelectLike } from "./inventory-ui";
import { formatDate, formatNumber } from "../utils/inventory.utils";

function normalizeStock(item = {}) {
  const qtyAvailable = Number(item.qtyAvailable ?? item.qty_available ?? 0);
  const qtyReserved = Number(item.qtyReserved ?? item.qty_reserved ?? 0);
  const qtyDamaged = Number(item.qtyDamaged ?? item.qty_damaged ?? 0);
  const expiredQty = Number(item.expiredQty ?? item.expired_qty ?? 0);
  const expiringBatchesCount = Number(
    item.expiringBatchesCount ?? item.expiring_batches_count ?? 0
  );
  const lowStockThreshold = Number(
    item.lowStockThreshold ?? item.low_stock_threshold ?? 0
  );

  return {
    ...item,
    productId: item.productId ?? item.product_id ?? "",
    name: item.name ?? "-",
    sku: item.sku ?? "-",
    barcode: item.barcode ?? "-",
    qtyAvailable,
    qtyReserved,
    qtyDamaged,
    expiredQty,
    expiringBatchesCount,
    lowStockThreshold,
    nearestExpiry: item.nearestExpiry ?? item.nearest_expiry ?? "",
    // freeQty: Math.max(0, qtyAvailable - qtyReserved),
  };
}

function normalizeBatch(batch = {}) {
  return {
    id: batch.id ?? "",
    batchNo: batch.batchNo ?? batch.batch_no ?? "-",
    mfgDate: batch.mfgDate ?? batch.mfg_date ?? "",
    expiryDate: batch.expiryDate ?? batch.expiry_date ?? "",
    qtyAvailable: Number(batch.qtyAvailable ?? batch.qty_available ?? 0),
    qtyReserved: Number(batch.qtyReserved ?? batch.qty_reserved ?? 0),
    qtyDamaged: Number(batch.qtyDamaged ?? batch.qty_damaged ?? 0),
    uom: batch.uom ?? "unit",
  };
}

function getStockStatus(item) {
  if (item.expiredQty > 0) {
    return { label: "Expired", className: "bg-rose-50 text-rose-600" };
  }
  if (item.qtyAvailable <= item.lowStockThreshold) {
    return { label: "Low Stock", className: "bg-amber-50 text-amber-700" };
  }
  if (item.expiringBatchesCount > 0) {
    return { label: "Expiring", className: "bg-orange-50 text-orange-700" };
  }
  return { label: "Healthy", className: "bg-emerald-50 text-emerald-600" };
}

function getBatchStatus(batch) {
  if (!batch.expiryDate) {
    return { label: "N/A", className: "bg-slate-100 text-slate-600" };
  }

  const now = new Date();
  const expiry = new Date(batch.expiryDate);
  const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (diff < 0) return { label: "Expired", className: "bg-rose-50 text-rose-600" };
  if (diff <= 30) return { label: "Near Expiry", className: "bg-amber-50 text-amber-700" };
  return { label: "Fresh", className: "bg-emerald-50 text-emerald-600" };
}

function MetricPill({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-slate-50 text-slate-700",
    success: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-rose-50 text-rose-700",
  };

  return (
    <div className={`rounded-[12px] px-3 py-2 ${tones[tone] || tones.default}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] opacity-70">
        {label}
      </div>
      <div className="mt-1 text-[14px] font-bold">{value}</div>
    </div>
  );
}

export default function InventoryTab({
  stockLoading,
  visibleStockItems,
  selectedStockProductId,
  setSelectedStockProductId,
  stockItems,
  warehouseFilter,
  batchesLoading,
  batches,
  pagination,
}) {
  const stocks = useMemo(
    () => (visibleStockItems || []).map(normalizeStock),
    [visibleStockItems]
  );

  const allStocks = useMemo(
    () => (stockItems || []).map(normalizeStock),
    [stockItems]
  );

  const selectedProduct = useMemo(
    () =>
      allStocks.find(
        (item) => String(item.productId) === String(selectedStockProductId)
      ) || null,
    [allStocks, selectedStockProductId]
  );

  const normalizedBatches = useMemo(
    () => (batches || []).map(normalizeBatch),
    [batches]
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.3fr_0.95fr]">
      <Card className="flex min-h-[560px] flex-col overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-[#f4d8dc] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[20px] font-bold text-slate-800">
              Assigned Product Stock
            </div>
            <div className="mt-1 text-[12px] text-slate-500">
              Compact inventory overview with the most important stock signals
            </div>
          </div>

          <div className="w-full max-w-[320px]">
            <SelectLike
              value={selectedStockProductId}
              onChange={setSelectedStockProductId}
              placeholder="Select product"
              searchable
              searchPlaceholder="Search product or SKU"
              options={allStocks.map((x) => ({
                value: x.productId,
                label: `${x.name}${x.sku ? ` (${x.sku})` : ""}`,
              }))}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {stockLoading ? (
            <div className="p-5 text-[13px] text-slate-500">Loading stock summary...</div>
          ) : stocks.length === 0 ? (
            <div className="p-5 text-[13px] text-slate-500">
              {warehouseFilter
                ? "No stock found for selected warehouse."
                : "No stock found across warehouses."}
            </div>
          ) : (
            <table className="w-full min-w-[860px]">
              <thead className="sticky top-0 z-10 bg-[#fff7f8] text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <tr>
                  <th className="px-5 py-3">Product</th>
                  <th className="px-4 py-3 text-center">Available</th>
                  <th className="px-4 py-3 text-center">Reserved</th>
                  <th className="px-4 py-3 text-center">Expiry</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>

              <tbody>
                {stocks.map((item) => {
                  const status = getStockStatus(item);
                  const active =
                    String(selectedStockProductId) === String(item.productId);

                  return (
                    <tr
                      key={item.productId}
                      onClick={() => setSelectedStockProductId(item.productId)}
                      className={
                        "cursor-pointer border-t border-[#f8dde1] align-top transition hover:bg-[#fff7f8] " +
                        (active ? "bg-[#fff3f5]" : "")
                      }
                    >
                      <td className="px-5 py-4">
                        <div className="max-w-[300px]">
                          <div className="truncate text-[14px] font-bold text-slate-800">
                            {item.name}
                          </div>
                          <div className="mt-1 text-[12px] text-slate-500">
                            SKU: {item.sku || "-"}
                          </div>
                          <div className="mt-1 truncate text-[12px] text-slate-400">
                            Barcode: {item.barcode || "-"}
                          </div>
                          <div className="mt-3 grid max-w-[340px] grid-cols-4 gap-2">
  <div className="flex min-h-[58px] flex-col items-center justify-center rounded-[10px] bg-slate-50 px-2 py-2 text-center">
    <div className="min-h-[22px] text-[9px] font-semibold uppercase leading-[11px] tracking-[0.08em] text-slate-400">
      Damaged
    </div>
    <div className="mt-1 text-[13px] font-bold text-slate-700">
      {formatNumber(item.qtyDamaged)}
    </div>
  </div>

  <div className="flex min-h-[58px] flex-col items-center justify-center rounded-[10px] bg-rose-50 px-2 py-2 text-center">
    <div className="min-h-[22px] text-[9px] font-semibold uppercase leading-[11px] tracking-[0.08em] text-rose-400">
      Expired
    </div>
    <div className="mt-1 text-[13px] font-bold text-rose-600">
      {formatNumber(item.expiredQty)}
    </div>
  </div>

  <div className="flex min-h-[58px] flex-col items-center justify-center rounded-[10px] bg-amber-50 px-2 py-2 text-center">
    <div className="min-h-[22px] text-[9px] font-semibold uppercase leading-[11px] tracking-[0.08em] text-amber-500">
      Exp Batch
    </div>
    <div className="mt-1 text-[13px] font-bold text-amber-700">
      {formatNumber(item.expiringBatchesCount)}
    </div>
  </div>

  <div className="flex min-h-[58px] flex-col items-center justify-center rounded-[10px] bg-[#fff3f5] px-2 py-2 text-center">
    <div className="min-h-[22px] text-[9px] font-semibold uppercase leading-[11px] tracking-[0.08em] text-[#d86a78]/70">
      Threshold
    </div>
    <div className="mt-1 text-[13px] font-bold text-[#d86a78]">
      {formatNumber(item.lowStockThreshold)}
    </div>
  </div>
</div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="text-[15px] font-bold text-slate-800">
                          {formatNumber(item.qtyAvailable)}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="text-[15px] font-semibold text-slate-700">
                          {formatNumber(item.qtyReserved)}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="text-[13px] font-medium text-slate-700">
                          {formatDate(item.nearestExpiry)}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination || null}
      </Card>

      <Card className="flex min-h-[560px] flex-col overflow-hidden">
        <div className="border-b border-[#f4d8dc] px-5 py-4">
          <div className="text-[20px] font-bold text-slate-800">Batch / FEFO View</div>
          <div className="mt-1 text-[12px] text-slate-500">
            Batch-wise expiry and quantity details for the selected product
          </div>
        </div>

        <div className="border-b border-[#f8dde1] bg-[#fffafb] px-5 py-4">
          {selectedProduct ? (
            <div>
              <div className="text-[16px] font-bold text-slate-800">
                {selectedProduct.name}
              </div>
              <div className="mt-1 text-[12px] text-slate-500">
                SKU: {selectedProduct.sku || "-"} • Barcode: {selectedProduct.barcode || "-"}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <MetricPill
                  label="Available"
                  value={formatNumber(selectedProduct.qtyAvailable)}
                />
                <MetricPill
                  label="Reserved"
                  value={formatNumber(selectedProduct.qtyReserved)}
                  tone="warning"
                />
                <MetricPill
                  label="Expired"
                  value={formatNumber(selectedProduct.expiredQty)}
                  tone="danger"
                />
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-slate-500">
              Select a product to view FEFO details.
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {!selectedStockProductId ? (
            <div className="text-[13px] text-slate-500">
              No product selected for batch details.
            </div>
          ) : !warehouseFilter ? (
            <div className="text-[13px] text-slate-500">
              Batch details are available only for a specific warehouse. Please select one warehouse.
            </div>
          ) : batchesLoading ? (
            <div className="text-[13px] text-slate-500">Loading batches...</div>
          ) : normalizedBatches.length === 0 ? (
            <div className="text-[13px] text-slate-500">
              No batches found for selected product.
            </div>
          ) : (
            <div className="space-y-3">
              {normalizedBatches.map((batch) => {
                const status = getBatchStatus(batch);

                return (
                  <div
                    key={batch.id || batch.batchNo}
                    className="rounded-[16px] border border-[#f4dde1] bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-[14px] font-bold text-slate-800">
                            Batch: {batch.batchNo}
                          </div>
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </div>

                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] text-slate-500">
                          <div>MFG: {formatDate(batch.mfgDate)}</div>
                          <div>Expiry: {formatDate(batch.expiryDate)}</div>
                          <div>Reserved: {formatNumber(batch.qtyReserved)}</div>
                          <div>Damaged: {formatNumber(batch.qtyDamaged)}</div>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="text-[18px] font-bold text-slate-800">
                          {formatNumber(batch.qtyAvailable)}
                        </div>
                        <div className="mt-1 text-[12px] font-medium text-slate-500">
                          {batch.uom}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}